import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {from, Observable} from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import {Layout, Layouts} from './layout';
import { ConfigService } from '@docma-mm-core/config/config.service';
import { LayoutField } from './layout-field';
import { catchError, map } from 'rxjs/operators';
import {Fault} from '@docma-mm-core/fault/fault';
import {ActivityData} from '@docma-mm-core/activity/activity';
import {ActivityEditData} from '@docma-mm-core/activity/activity-edit';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {FaultData} from '@docma-mm-core/fault/fault-data';


@Injectable()
export class LayoutApiService extends BasedataService {

  private projectUrl = '/projects/';
  private layoutUrl = '/layouts';
  private configurationLayoutUrl = '/configuration/layouts';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getLayoutUrl() {
    return super.getFullUrl(this.layoutUrl);
  }

  private getProjectLayoutUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.layoutUrl);
  }

  // get layouts
  getLayouts(top: number = 10000, skip: number = 0): Observable<Layouts> {
    const endpoint = this.getLayoutUrl();
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    return this.http
      .get<Layouts>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get layouts by project
  getProjectLayouts(projectId: number, top: number = 10000, skip: number = 0): Observable<Layouts> {
    const endpoint = this.getProjectLayoutUrl(projectId);
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    httpParams = httpParams.set('onlyFavoriteWithPermission', 'false');
    return this.http
      .get<Layouts>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  getLayoutFields(projectId: number, layoutId: number): Observable<LayoutField[]> {
    const endpoint = `${this.getProjectLayoutUrl(projectId)}/${layoutId}/userfields`;
    const httpParams = new HttpParams();

    return this.http
      .get<LayoutField[]>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(map((fs: []) => fs.map(f => new LayoutField(f))),
        catchError(e  => this.handleError(e)));
  }

  getFullLayout(layoutId?: number): Observable<Layout> {
    if (!layoutId) {
      return from(Promise.resolve(new Layout()));
    }

    const endpoint = `${super.getFullUrl(this.configurationLayoutUrl)}/${layoutId}`;
    let httpParams = new HttpParams();
    httpParams = httpParams.set('$details', '10');

    return this.http
      .get<Layout>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  getHasReferences(layoutId?: number): Observable<boolean> {
    const endpoint = `${super.getFullUrl(this.configurationLayoutUrl)}/${layoutId}/hasReferences`;
    return this.http
      .get<boolean>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  createLayout(layout: Layout) {
    const endpoint = `${super.getFullUrl(this.configurationLayoutUrl)}`;
    return this.http
      .post<Layout>(endpoint, JSON.stringify(layout), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  patchLayout(layoutId: number, patches: any[]): Observable<Layout> {
    const endpoint = `${super.getFullUrl(this.configurationLayoutUrl)}/${layoutId}`;
    return this.http
      .patch<Layout>(endpoint, JSON.stringify(patches), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  deleteLayout(layoutId?: number): Observable<any> {
    const endpoint = `${super.getFullUrl(this.configurationLayoutUrl)}/${layoutId}`;
    return this.http
      .delete(endpoint, { headers: this.getHeaders(), params: new HttpParams() })
      .pipe(catchError(e  => this.handleError(e)));
  }

}
